import type { BomResponse, ItemCategory } from "@stocks/shared";

export interface DraftOrderLine {
  productId: string;
  optionId: string;
  quantity: number;
}

export interface DraftMaterial {
  itemId: string;
  itemCode: string;
  itemName: string;
  itemCategory: ItemCategory | null;
  itemSpec: string | null;
  baseUnit: string;
  required: number;
  available: number;
  shortage: number;
}

/** 부동소수점 오차 제거 (소수 6자리) */
const round = (n: number) => Math.round(n * 1e6) / 1e6;

/**
 * 입력 중인 발주 행으로 필요 자재를 합산한다 (서버 aggregateRequired 와 동일 규칙).
 * 필요 수량 = Σ(발주 수량 × 한 벌당 소요량), 옵션 지정 BOM 행은 해당 옵션에만 적용.
 */
export const aggregateDraftRequired = (
  lines: DraftOrderLine[],
  boms: Map<string, BomResponse | null | undefined>,
) => {
  const map = new Map<string, Omit<DraftMaterial, "available" | "shortage">>();
  for (const line of lines) {
    if (!line.productId || !line.optionId || !(line.quantity > 0)) continue;
    for (const bom of boms.get(line.productId)?.lines ?? []) {
      if (bom.optionId !== null && bom.optionId !== line.optionId) continue;
      const amount = Number(bom.quantityPerUnit) * line.quantity;
      const prev = map.get(bom.itemId);
      map.set(bom.itemId, {
        itemId: bom.itemId,
        itemCode: bom.itemCode,
        itemName: bom.itemName,
        itemCategory: bom.itemCategory,
        itemSpec: bom.itemSpec,
        baseUnit: bom.baseUnit,
        required: round((prev?.required ?? 0) + amount),
      });
    }
  }
  return [...map.values()];
};

/** 필요 수량에 가용 수량을 대조해 부족 수량(max(0, 필요 − 가용))을 계산 */
export const withShortage = (
  required: ReturnType<typeof aggregateDraftRequired>,
  availability: Map<string, number>,
): DraftMaterial[] =>
  required.map((r) => {
    const available = availability.get(r.itemId) ?? 0;
    return { ...r, available, shortage: round(Math.max(0, r.required - available)) };
  });
